import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useOrpc, type OrpcClient } from '@/hooks/useClient'
import { RENDERING_PROVIDER_MISSING_CODE } from '@/model/middleware-types'

export type ChargeQuoteResult = Awaited<ReturnType<OrpcClient['billingQuoteCharge']>>
export type ChargeQuoteLine = ChargeQuoteResult['quotes'][number]

/**
 * Submitted charges for the note's billing codes (PD-1597): the org catalog price
 * tiered by the rendering provider's credential, quoted by the middleware. The
 * query key carries every input the charge depends on, so a code, provider or
 * organization change recalculates on its own. `encounterId` makes the server
 * price for the encounter's organization — the one save and Lock judge — rather
 * than the viewer's (PD-1690). `priceFor` is undefined only
 * while a code has no quote yet; a quoted line that carries no charge (RN,
 * minor, unpriced code) is 0, so callers' catalog fallback never prices a line
 * the policy refused.
 *
 * `isQuoting` is true only while a request is actually in flight: a quote that
 * FAILED leaves it false, so a billing save never depends on a healthy quote
 * endpoint — the server refuses a bad line at save and at Lock regardless.
 *
 * `refusedFor` separates the one reason that forbids billing the code at all
 * from the reasons that merely leave it unpriced (PD-1647): a $0 line and a line
 * the credential may never bill are both `submittedCharge: null`, but only the
 * second must block the save.
 */
export function useSubmittedChargeQuote(input: {
  codes: string[]
  practitionerId?: string
  patientId?: string
  encounterId?: string
  organizationId?: string
}): {
  quote: ChargeQuoteResult | undefined
  priceFor: (code: string) => number | undefined
  refusedFor: (code: string) => ChargeQuoteLine['reason'] | undefined
  /** A quote is in flight; a refusal for a just-picked code may not have arrived yet. */
  isQuoting: boolean
  /** The server's refusal of the quote itself (e.g. a deleted rendering provider, PD-2017). */
  quoteError: string | undefined
  /** `quoteError` is the deleted/unfound rendering-provider refusal: the visit cannot be billed. */
  providerMissing: boolean
  /** The rendering provider was removed and the quote used their saved record (PD-2017). */
  providerRemoved: boolean
  providerDisplay: string | undefined
} {
  const orpc = useOrpc()
  const codes = useMemo(() => [...new Set(input.codes.filter(Boolean))].sort(), [input.codes])
  const enabled = codes.length > 0 && Boolean(input.practitionerId) && Boolean(input.patientId)
  const { data, isFetching, error } = useQuery(
    orpc.billingQuoteCharge.queryOptions({
      input: {
        codes,
        practitionerId: input.practitionerId ?? '',
        patientId: input.patientId ?? '',
        encounterId: input.encounterId,
        organizationId: input.organizationId,
      },
      enabled,
      staleTime: 60_000,
    }),
  )
  const priceFor = useMemo(
    () => (code: string) => {
      const line = data?.quotes.find((q) => q.code === code)
      return line ? (line.submittedCharge ?? 0) : undefined
    },
    [data],
  )
  const refusedFor = useMemo(
    () => (code: string) => {
      const line = data?.quotes.find((q) => q.code === code)
      return line?.reason === 'credential-cannot-bill' ? line.reason : undefined
    },
    [data],
  )
  // Only a 400 carries a message written for the user; anything else stays silent here.
  const refusal = enabled ? (error as { status?: number; data?: { code?: unknown } } | null) : null
  const quoteError = refusal?.status === 400 ? error?.message : undefined
  const providerMissing =
    refusal?.status === 400 && refusal.data?.code === RENDERING_PROVIDER_MISSING_CODE
  return {
    quote: data,
    priceFor,
    refusedFor,
    isQuoting: enabled && isFetching,
    quoteError,
    providerMissing,
    providerRemoved: data?.provider.removed === true,
    providerDisplay: data?.provider.display ?? undefined,
  }
}
